/**
 * playerStore.ts
 * =============================================================================
 * REWRITTEN: Simplified Zustand store for player state management.
 *
 * Key Changes:
 * - Store does NOT "think" - it reflects Engine state
 * - setMode does NOT auto-play
 * - Clear separation between UI state and engine state
 * - Operation locks prevent race conditions at UI level
 * - [v2.1] Added pausedWithMode tracking for mode-change detection
 * - [v2.2] Fixed smart resume click bug using playFromPaused()
 * - [v2.2] Fixed sticky ambience bug with track clearing
 * - [v2.5] Added Generation ID pattern to detect stale loads
 *
 * @author Neuro-Audio Lab
 * @version 2.5.0 - Generation ID pattern for stale request detection
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AudioEngine, getAudioEngine } from '../core/AudioEngine';
import type { AudioMode, TrackId, EnginePublicState, EngineState } from '../core/AudioEngine';
import type { TimerProfileId, TimerState as TimerStateType } from '../core/types';
import { TIMER_PROFILES } from '../core/types';
import { MODE_CONFIGS } from '../config/audioPresets';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface TrackVolumes {
  ambience: number;
  tone: number;
  voice: number;
}

export interface SessionStats {
  totalSessions: number;
  totalMinutes: number;
  lastSessionDate: string | null;
  favoriteMode: AudioMode | null;
}

/**
 * Complete player store state
 */
export interface PlayerState {
  // === Engine State (synced from AudioEngine) ===
  isInitialized: boolean;
  engineState: EngineState;
  isPlaying: boolean;
  isPaused: boolean;
  isTransitioning: boolean;
  currentMode: AudioMode | null;

  // === [v2.1] Mode tracking for pause/resume ===
  // Stores the mode that was active when pause was triggered
  pausedWithMode: AudioMode | null;

  // === Volume ===
  volumes: TrackVolumes;
  masterVolume: number;
  isMuted: boolean;
  previousMasterVolume: number;

  // === Track Loading ===
  tracksLoaded: {
    ambience: boolean;
    voice: boolean;
    entrainment: boolean; // [v2.8] File-based entrainment track
  };

  // === Timer (New System v3.0) ===
  timerState: TimerStateType; // 'idle' | 'running' | 'paused' | 'break'
  currentTimerProfile: TimerProfileId; // 'free' | 'pomodoro' | 'ultradian' | 'sleep_timer'
  sessionElapsedTime: number; // Master counter (always counts up) in seconds
  remainingTime: number; // For countdown timers (pomodoro/sleep) in seconds
  isInBreak: boolean; // True during break periods

  // === Session Stats ===
  sessionStats: SessionStats;

  // === UI State ===
  isLoading: boolean;
  error: string | null;
  showMixer: boolean;

  // === Operation Lock ===
  // Prevents UI from triggering multiple operations while one is in progress
  operationInProgress: boolean;
}

/**
 * Player store actions
 */
export interface PlayerActions {
  // Initialization
  initializeEngine: () => Promise<void>;

  // Playback - These are the ONLY ways to control audio
  play: () => Promise<void>;
  playWithMode: (mode: AudioMode) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  stop: () => Promise<void>;
  togglePlayPause: () => Promise<void>;

  // Mode selection - Does NOT auto-play
  setMode: (mode: AudioMode) => void;

  // Volume control
  setTrackVolume: (trackId: TrackId, volume: number) => void;
  setMasterVolume: (volume: number) => void;
  toggleMute: () => void;

  // Track loading
  loadAmbience: (url: string) => Promise<void>;
  loadVoice: (url: string) => Promise<void>;
  loadEntrainment: (url: string) => Promise<void>; // [v2.8] File-based entrainment
  loadTrigger: (url: string) => Promise<void>;      // [v2.9] Pavlovian one-shot trigger
  // [v2.2] Clear tracks
  clearAmbience: () => void;
  clearVoice: () => void;
  clearEntrainment: () => void; // [v2.8]
  clearTrigger: () => void;     // [v2.9]

  // Timer control (New System v3.0)
  setTimerState: (state: TimerStateType) => void;
  setTimerProfile: (profileId: TimerProfileId) => void;
  tickSessionTime: () => void; // Increment sessionElapsedTime by 1
  tickRemainingTime: () => void; // Decrement remainingTime by 1
  resetSession: () => void; // Reset all timer state to idle/0
  toggleBreak: () => void; // Toggle break state

  // UI actions
  toggleMixer: () => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Session tracking
  recordSession: (mode: AudioMode, minutes: number) => void;

  // Engine sync (internal)
  syncWithEngine: (state: EnginePublicState) => void;

  // Cleanup
  dispose: () => void;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: PlayerState = {
  isInitialized: false,
  engineState: 'idle',
  isPlaying: false,
  isPaused: false,
  isTransitioning: false,
  currentMode: null,

  // [v2.1] Initialize pausedWithMode tracking
  pausedWithMode: null,

  volumes: {
    ambience: 0.6,
    tone: 0.6,
    voice: 0.6,
  },
  masterVolume: 0.6,
  isMuted: false,
  previousMasterVolume: 0.6,

  tracksLoaded: {
    ambience: false,
    voice: false,
    entrainment: false, // [v2.8]
  },

  // Timer (New System v3.0)
  timerState: 'idle',
  currentTimerProfile: 'free',
  sessionElapsedTime: 0,
  remainingTime: 0,
  isInBreak: false,

  sessionStats: {
    totalSessions: 0,
    totalMinutes: 0,
    lastSessionDate: null,
    favoriteMode: null,
  },

  isLoading: false,
  error: null,
  showMixer: false,
  operationInProgress: false,
};

// =============================================================================
// STORE CREATION
// =============================================================================

export const usePlayerStore = create<PlayerState & PlayerActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // =====================================================================
        // INITIALIZATION
        // =====================================================================

        initializeEngine: async () => {
          const state = get();
          if (state.isInitialized) {
            console.log('[PlayerStore] Already initialized');
            return;
          }

          set({ isLoading: true, error: null });

          try {
            const engine = getAudioEngine();
            await engine.initialize({
              masterVolume: state.masterVolume,
            });

            // Subscribe to engine state changes
            engine.subscribe((engineState) => {
              get().syncWithEngine(engineState);
            });

            // Apply saved volumes
            engine.setMasterVolume(state.masterVolume);
            engine.setTrackVolume('ambience', state.volumes.ambience);
            engine.setTrackVolume('tone', state.volumes.tone);
            engine.setTrackVolume('voice', state.volumes.voice);

            set({
              isInitialized: true,
              isLoading: false,
            });

            console.log('[PlayerStore] Initialized');
          } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            set({
              error: `Init failed: ${msg}`,
              isLoading: false,
            });
            console.error('[PlayerStore] Init failed:', error);
          }
        },

        // =====================================================================
        // PLAYBACK CONTROL
        // =====================================================================

        /**
         * Play with the currently selected mode.
         * If no mode is selected, defaults to 'focus'.
         */
        play: async () => {
          const state = get();
          if (!state.isInitialized || state.operationInProgress) return;

          const mode = state.currentMode ?? 'focus';
          set({ operationInProgress: true });

          try {
            const engine = getAudioEngine();
            await engine.play(mode);

            // [v2.1] Clear pausedWithMode since we're starting fresh
            set({ pausedWithMode: null });

            // NOTE: Timer is now independent - UI controls timer separately
          } catch (err) {
            console.error('[PlayerStore] Play failed:', err);
            set({ error: 'Failed to play' });
          } finally {
            set({ operationInProgress: false });
          }
        },

        /**
         * Play with a specific mode (used when user clicks a mode card AND play).
         */
        playWithMode: async (mode: AudioMode) => {
          const state = get();
          if (!state.isInitialized || state.operationInProgress) return;

          set({ operationInProgress: true, currentMode: mode });

          try {
            const engine = getAudioEngine();
            await engine.play(mode);

            // [v2.1] Clear pausedWithMode since we're starting fresh
            set({ pausedWithMode: null });

            // NOTE: Timer is now independent - UI controls timer separately
          } catch (err) {
            console.error('[PlayerStore] PlayWithMode failed:', err);
            set({ error: 'Failed to play' });
          } finally {
            set({ operationInProgress: false });
          }
        },

        pause: async () => {
          const state = get();
          if (!state.isInitialized || state.operationInProgress) return;

          set({ operationInProgress: true });

          try {
            const engine = getAudioEngine();
            await engine.pause();

            // [v2.1] CRITICAL: Store the mode we paused with
            // This allows resume() to detect if mode changed while paused
            set({
              pausedWithMode: state.currentMode,
            });

            // NOTE: Timer is now independent - UI controls timer separately
            console.log('[PlayerStore] Paused with mode:', state.currentMode);
          } catch (err) {
            console.error('[PlayerStore] Pause failed:', err);
          } finally {
            set({ operationInProgress: false });
          }
        },

        /**
         * Resume playback from pause.
         * [v2.2] SMART RESUME: If mode changed while paused, use playFromPaused()
         * for click-free transitions.
         */
        resume: async () => {
          const state = get();
          if (!state.isInitialized || state.operationInProgress) return;

          set({ operationInProgress: true });

          try {
            const engine = getAudioEngine();

            // [v2.2] Check if mode changed while we were paused
            const modeChangedDuringPause =
              state.pausedWithMode !== null &&
              state.currentMode !== state.pausedWithMode;

            if (modeChangedDuringPause) {
              // [v2.2] Mode changed during pause - use the specialized method
              // This handles the suspended AudioContext properly to avoid clicks
              console.log('[PlayerStore] Mode changed during pause, using playFromPaused', {
                pausedWithMode: state.pausedWithMode,
                currentMode: state.currentMode,
              });

              // Use the new playFromPaused method that handles click prevention
              await engine.playFromPaused(state.currentMode ?? 'focus');

              // Clear the pausedWithMode tracker
              set({ pausedWithMode: null });
            } else {
              // Same mode - simple resume
              await engine.resume();
              set({ pausedWithMode: null });
            }

            // NOTE: Timer is now independent - UI controls timer separately
          } catch (err) {
            console.error('[PlayerStore] Resume failed:', err);
          } finally {
            set({ operationInProgress: false });
          }
        },

        stop: async () => {
          const state = get();
          if (!state.isInitialized || state.operationInProgress) return;

          // [v2.1] Allow stop from ANY non-idle state (playing OR paused)
          if (state.engineState === 'idle') {
            console.log('[PlayerStore] Already idle, nothing to stop');
            return;
          }

          set({ operationInProgress: true });

          try {
            const engine = getAudioEngine();
            await engine.stop();

            // Record session if there was progress (using new timer state)
            if (state.currentMode && state.sessionElapsedTime > 0) {
              const playedMinutes = Math.round(state.sessionElapsedTime / 60);
              if (playedMinutes > 0) {
                get().recordSession(state.currentMode, playedMinutes);
              }
            }

            // [v2.1] Clear pausedWithMode on stop
            // NOTE: Timer reset is now independent - UI controls timer separately
            set({
              pausedWithMode: null,
            });
          } catch (err) {
            console.error('[PlayerStore] Stop failed:', err);
          } finally {
            set({ operationInProgress: false });
          }
        },

        /**
         * Toggle between play/pause based on current state.
         */
        togglePlayPause: async () => {
          const state = get();
          if (state.operationInProgress) return;

          if (state.isPaused) {
            await get().resume();
          } else if (state.isPlaying) {
            await get().pause();
          } else {
            await get().play();
          }
        },

        // =====================================================================
        // MODE SELECTION - Does NOT auto-play
        // =====================================================================

        /**
         * Set the selected mode WITHOUT starting playback.
         * This allows users to browse modes without audio glitches.
         */
        setMode: (mode: AudioMode) => {
          const engine = getAudioEngine();
          engine.setMode(mode);

          // [v3.1] Apply mode-specific default ambience volume.
          // Each mode has a different spectral density (dense brown noise vs.
          // sparse river), so we compensate by setting a per-mode starting
          // volume. The value in MODE_CONFIGS is 0-100; we convert to 0-1.
          const config = MODE_CONFIGS[mode];
          const ambienceVolume = config.defaultAmbienceVolume / 100;
          engine.setTrackVolume('ambience', ambienceVolume);

          set({
            currentMode: mode,
            // Sync the store's volume state so the UI fader reflects the change
            volumes: {
              ...get().volumes,
              ambience: ambienceVolume,
            },
          });

          console.log('[PlayerStore] Mode selected (no auto-play):', mode, {
            defaultAmbienceVolume: config.defaultAmbienceVolume + '%',
          });
        },

        // =====================================================================
        // VOLUME CONTROL
        // =====================================================================

        setTrackVolume: (trackId: TrackId, volume: number) => {
          const clampedVolume = Math.max(0, Math.min(1, volume));
          const engine = getAudioEngine();
          engine.setTrackVolume(trackId, clampedVolume);

          set({
            volumes: {
              ...get().volumes,
              [trackId]: clampedVolume,
            },
          });
        },

        setMasterVolume: (volume: number) => {
          const clampedVolume = Math.max(0, Math.min(1, volume));
          const engine = getAudioEngine();
          engine.setMasterVolume(clampedVolume);

          set({
            masterVolume: clampedVolume,
            isMuted: clampedVolume === 0,
          });
        },

        toggleMute: () => {
          const state = get();
          const engine = getAudioEngine();

          if (state.isMuted) {
            const restoreVolume = state.previousMasterVolume || 0.8;
            engine.setMasterVolume(restoreVolume);
            set({
              isMuted: false,
              masterVolume: restoreVolume,
            });
          } else {
            engine.setMasterVolume(0);
            set({
              isMuted: true,
              previousMasterVolume: state.masterVolume,
              masterVolume: 0,
            });
          }
        },

        // =====================================================================
        // TRACK LOADING
        // [v2.5] Now passes playId for stale request detection
        // =====================================================================

        loadAmbience: async (url: string) => {
          const engine = getAudioEngine();
          // [v2.5] Capture the current generation ID before starting load
          const playId = engine.getCurrentPlayId();

          set({ isLoading: true, error: null });

          try {
            // [v2.5] Pass playId to loadTrack for stale detection
            await engine.loadTrack(
              'ambience',
              {
                url,
                loop: true,
                volume: get().volumes.ambience,
              },
              playId
            );

            // [v2.5] Only update state if load wasn't discarded as stale
            // Check if playId is still current (load may have been silently aborted)
            if (engine.getCurrentPlayId() === playId) {
              set({
                isLoading: false,
                tracksLoaded: { ...get().tracksLoaded, ambience: true },
              });
            } else {
              // Load was discarded - just clear loading state
              set({ isLoading: false });
              console.log('[PlayerStore] Ambience load completed but was stale - state not updated');
            }
          } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown';
            set({
              error: `Load ambience failed: ${msg}`,
              isLoading: false,
            });
          }
        },

        loadVoice: async (url: string) => {
          const engine = getAudioEngine();
          // [v2.5] Capture the current generation ID before starting load
          const playId = engine.getCurrentPlayId();

          set({ isLoading: true, error: null });

          try {
            // [v2.5] Pass playId to loadTrack for stale detection
            await engine.loadTrack(
              'voice',
              {
                url,
                loop: false,
                volume: get().volumes.voice,
              },
              playId
            );

            // [v2.5] Only update state if load wasn't discarded as stale
            if (engine.getCurrentPlayId() === playId) {
              set({
                isLoading: false,
                tracksLoaded: { ...get().tracksLoaded, voice: true },
              });
            } else {
              set({ isLoading: false });
              console.log('[PlayerStore] Voice load completed but was stale - state not updated');
            }
          } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown';
            set({
              error: `Load voice failed: ${msg}`,
              isLoading: false,
            });
          }
        },

        /**
         * [v2.8] Load the entrainment track (functional music file).
         * Uses Generation ID for stale detection - identical pattern to loadAmbience.
         */
        loadEntrainment: async (url: string) => {
          const engine = getAudioEngine();
          const playId = engine.getCurrentPlayId();

          set({ isLoading: true, error: null });

          try {
            await engine.loadEntrainment(url, playId);

            if (engine.getCurrentPlayId() === playId) {
              set({
                isLoading: false,
                tracksLoaded: { ...get().tracksLoaded, entrainment: true },
              });
            } else {
              set({ isLoading: false });
              console.log('[PlayerStore] Entrainment load completed but was stale - state not updated');
            }
          } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown';
            set({
              error: `Load entrainment failed: ${msg}`,
              isLoading: false,
            });
          }
        },

        // =====================================================================
        // [v2.2] TRACK CLEARING - Fix for "sticky ambience" bug
        // =====================================================================

        /**
         * Clear the ambience track.
         * Use this when switching to a mode that doesn't have ambience.
         */
        clearAmbience: () => {
          const engine = getAudioEngine();
          engine.clearTrack('ambience');

          set({
            tracksLoaded: { ...get().tracksLoaded, ambience: false },
          });

          console.log('[PlayerStore] Ambience cleared');
        },

        /**
         * Clear the voice track.
         */
        clearVoice: () => {
          const engine = getAudioEngine();
          engine.clearTrack('voice');

          set({
            tracksLoaded: { ...get().tracksLoaded, voice: false },
          });

          console.log('[PlayerStore] Voice cleared');
        },

        /**
         * [v2.8] Clear the entrainment track.
         * Used when switching to a mode that has no entrainment file.
         */
        clearEntrainment: () => {
          const engine = getAudioEngine();
          engine.clearTrack('entrainment');

          set({
            tracksLoaded: { ...get().tracksLoaded, entrainment: false },
          });

          console.log('[PlayerStore] Entrainment cleared');
        },

        /**
         * [v2.9] Load the one-shot trigger sound (e.g. Tibetan bowl).
         * Uses Generation ID for stale detection.
         */
        loadTrigger: async (url: string) => {
          const engine = getAudioEngine();
          const playId = engine.getCurrentPlayId();

          try {
            await engine.loadTrigger(url, playId);
            console.log('[PlayerStore] Trigger loaded');
          } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown';
            set({ error: `Load trigger failed: ${msg}` });
          }
        },

        /**
         * [v2.9] Clear the trigger buffer.
         * Used when switching to a mode that has no trigger.
         */
        clearTrigger: () => {
          const engine = getAudioEngine();
          engine.clearTrigger();
          console.log('[PlayerStore] Trigger cleared');
        },

        // =====================================================================
        // TIMER CONTROL (New System v3.0)
        // =====================================================================

        setTimerState: (state: TimerStateType) => {
          set({ timerState: state });
        },

        setTimerProfile: (profileId: TimerProfileId) => {
          const profile = TIMER_PROFILES[profileId];
          set({
            currentTimerProfile: profileId,
            remainingTime: profile.duration, // Initialize remaining time for countdown profiles
          });
        },

        tickSessionTime: () => {
          set((state) => ({
            sessionElapsedTime: state.sessionElapsedTime + 1,
          }));
        },

        tickRemainingTime: () => {
          set((state) => ({
            remainingTime: Math.max(0, state.remainingTime - 1),
          }));
        },

        resetSession: () => {
          const state = get();
          const profile = TIMER_PROFILES[state.currentTimerProfile];

          set({
            timerState: 'idle',
            sessionElapsedTime: 0,
            // CRITICAL: Restore the current profile's duration, don't just zero it out
            remainingTime: profile.duration,
            isInBreak: false,
          });
        },

        toggleBreak: () => {
          set((state) => ({
            isInBreak: !state.isInBreak,
          }));
        },

        // =====================================================================
        // UI ACTIONS
        // =====================================================================

        toggleMixer: () => {
          set({ showMixer: !get().showMixer });
        },

        setError: (error: string | null) => {
          set({ error });
        },

        clearError: () => {
          set({ error: null });
        },

        // =====================================================================
        // SESSION TRACKING
        // =====================================================================

        recordSession: (mode: AudioMode, minutes: number) => {
          const stats = get().sessionStats;

          set({
            sessionStats: {
              totalSessions: stats.totalSessions + 1,
              totalMinutes: stats.totalMinutes + minutes,
              lastSessionDate: new Date().toISOString(),
              favoriteMode: mode,
            },
          });

          console.log('[PlayerStore] Session recorded', { mode, minutes });
        },

        // =====================================================================
        // ENGINE SYNC
        // =====================================================================

        /**
         * Sync store state with engine state.
         * Called by engine subscription.
         */
        syncWithEngine: (engineState: EnginePublicState) => {
          set({
            isInitialized: engineState.isInitialized,
            engineState: engineState.engineState,
            isPlaying: engineState.isPlaying,
            isPaused: engineState.isPaused,
            isTransitioning: engineState.isTransitioning,
            currentMode: engineState.currentMode,
            volumes: {
              ambience: engineState.tracks.ambience.volume,
              tone: engineState.tracks.tone.volume,
              voice: engineState.tracks.voice.volume,
            },
            masterVolume: engineState.master.volume,
            tracksLoaded: {
              ambience: engineState.tracks.ambience.isLoaded,
              voice: engineState.tracks.voice.isLoaded,
              entrainment: engineState.tracks.tone.isLoaded, // [v2.8] tone = entrainment
            },
          });
        },

        // =====================================================================
        // CLEANUP
        // =====================================================================

        dispose: () => {
          const engine = getAudioEngine();
          engine.dispose();
          AudioEngine.resetInstance();

          set(initialState);
          console.log('[PlayerStore] Disposed');
        },
      }),
      {
        name: 'neuro-audio-player-v3',
        partialize: (state) => ({
          volumes: state.volumes,
          masterVolume: state.masterVolume,
          sessionStats: state.sessionStats,
          currentTimerProfile: state.currentTimerProfile,
        }),
      }
    ),
    { name: 'PlayerStore' }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectVolumes = (state: PlayerState & PlayerActions) => ({
  volumes: state.volumes,
  masterVolume: state.masterVolume,
  isMuted: state.isMuted,
});

export const selectPlayback = (state: PlayerState & PlayerActions) => ({
  isPlaying: state.isPlaying,
  isPaused: state.isPaused,
  isTransitioning: state.isTransitioning,
  currentMode: state.currentMode,
  operationInProgress: state.operationInProgress,
  engineState: state.engineState, // [v2.1] Added for UI logic
});

export const selectTimer = (state: PlayerState & PlayerActions) => ({
  timerState: state.timerState,
  currentTimerProfile: state.currentTimerProfile,
  sessionElapsedTime: state.sessionElapsedTime,
  remainingTime: state.remainingTime,
  isInBreak: state.isInBreak,
});

export const selectInitState = (state: PlayerState & PlayerActions) => ({
  isInitialized: state.isInitialized,
  isLoading: state.isLoading,
  error: state.error,
});

// =============================================================================
// HOOKS
// =============================================================================

export const useVolumeControls = () => {
  const volumes = usePlayerStore(selectVolumes);
  const setTrackVolume = usePlayerStore((s) => s.setTrackVolume);
  const setMasterVolume = usePlayerStore((s) => s.setMasterVolume);
  const toggleMute = usePlayerStore((s) => s.toggleMute);

  return {
    ...volumes,
    setTrackVolume,
    setMasterVolume,
    toggleMute,
  };
};

export const usePlaybackControls = () => {
  const playback = usePlayerStore(selectPlayback);
  const play = usePlayerStore((s) => s.play);
  const playWithMode = usePlayerStore((s) => s.playWithMode);
  const pause = usePlayerStore((s) => s.pause);
  const resume = usePlayerStore((s) => s.resume);
  const stop = usePlayerStore((s) => s.stop);
  const togglePlayPause = usePlayerStore((s) => s.togglePlayPause);
  const setMode = usePlayerStore((s) => s.setMode);

  return {
    ...playback,
    play,
    playWithMode,
    pause,
    resume,
    stop,
    togglePlayPause,
    setMode,
  };
};

export const useTimerControls = () => {
  const timer = usePlayerStore(selectTimer);
  const setTimerState = usePlayerStore((s) => s.setTimerState);
  const setTimerProfile = usePlayerStore((s) => s.setTimerProfile);
  const tickSessionTime = usePlayerStore((s) => s.tickSessionTime);
  const tickRemainingTime = usePlayerStore((s) => s.tickRemainingTime);
  const resetSession = usePlayerStore((s) => s.resetSession);
  const toggleBreak = usePlayerStore((s) => s.toggleBreak);

  return {
    ...timer,
    setTimerState,
    setTimerProfile,
    tickSessionTime,
    tickRemainingTime,
    resetSession,
    toggleBreak,
  };
};

// [v2.2] Hook for track management
export const useTrackControls = () => {
  const tracksLoaded = usePlayerStore((s) => s.tracksLoaded);
  const loadAmbience = usePlayerStore((s) => s.loadAmbience);
  const loadVoice = usePlayerStore((s) => s.loadVoice);
  const clearAmbience = usePlayerStore((s) => s.clearAmbience);
  const clearVoice = usePlayerStore((s) => s.clearVoice);

  return {
    tracksLoaded,
    loadAmbience,
    loadVoice,
    clearAmbience,
    clearVoice,
  };
};

export default usePlayerStore;